import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AgentAccessService } from "@/services/agent/AgentAccessService";
import {
  AgentAccessGrant,
  AgentAccessGrantWithToken,
  AgentUser,
  CreateGrantData,
  SetAccessPasswordData,
  UpdateGrantData,
} from "@/types/agent/agent-access";

const GRANTS_KEY = (agentId: string) => ["agent-access-grants", agentId];
const TEAM_ACCESS_KEY = (agentId: string) => ["agent-team-access", agentId];
const PASSWORD_KEY = (agentId: string) => ["agent-access-password", agentId];

// --- Access Grants ---

export const useAgentAccessGrants = (agentId: string) =>
  useQuery({
    queryKey: GRANTS_KEY(agentId),
    queryFn: () => AgentAccessService.listGrants(agentId),
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
    refetchOnMount: "stale",
    refetchOnWindowFocus: "stale",
  });

export const useCreateAccessGrant = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGrantData) => AgentAccessService.createGrant(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GRANTS_KEY(agentId) });
      toast.success("Access link created");
    },
    onError: () => toast.error("Failed to create access link"),
  });
};

export const useUpdateAccessGrant = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ grantId, data }: { grantId: number; data: UpdateGrantData }) =>
      AgentAccessService.updateGrant(grantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GRANTS_KEY(agentId) });
      toast.success("Access link updated");
    },
    onError: () => toast.error("Failed to update access link"),
  });
};

export const useDeleteAccessGrant = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grantId: number) => AgentAccessService.deleteGrant(grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GRANTS_KEY(agentId) });
      toast.success("Access link revoked");
    },
    onError: () => toast.error("Failed to revoke access link"),
  });
};

// --- Access Password ---

export const useAgentAccessPassword = (agentId: string) =>
  useQuery({
    queryKey: PASSWORD_KEY(agentId),
    queryFn: () => AgentAccessService.getPassword(agentId),
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000,
  });

export const useSetAccessPassword = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SetAccessPasswordData) => AgentAccessService.setPassword(agentId, data),
    onSuccess: (password, data) => {
      queryClient.setQueryData(PASSWORD_KEY(agentId), password);
      toast.success(data.password ? "Password saved" : "Unlock duration updated");
    },
    onError: () => toast.error("Failed to save the password"),
  });
};

export const useRemoveAccessPassword = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => AgentAccessService.removePassword(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PASSWORD_KEY(agentId) });
      toast.success("Password removed");
    },
    onError: () => toast.error("Failed to remove the password"),
  });
};

// --- Team Access ---

export const useAgentTeamAccess = (agentId: string) =>
  useQuery({
    queryKey: TEAM_ACCESS_KEY(agentId),
    queryFn: () => AgentAccessService.listTeamAccess(agentId),
    enabled: !!agentId,
    staleTime: Infinity,
  });

export const useShareWithTeam = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, expiresAt }: { teamId: number; expiresAt?: string }) =>
      AgentAccessService.shareWithTeam(teamId, agentId, expiresAt),
    onSuccess: ({ granted }) => {
      queryClient.invalidateQueries({ queryKey: TEAM_ACCESS_KEY(agentId) });
      toast.success(
        `Shared with ${granted} team member${granted !== 1 ? "s" : ""}`
      );
    },
    onError: () => toast.error("Failed to share with team"),
  });
};

export const useGrantMemberAccess = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, expiresAt }: { userId: string; expiresAt?: string }) =>
      AgentAccessService.grantMemberAccess(agentId, userId, expiresAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_ACCESS_KEY(agentId) });
      toast.success("Access granted");
    },
    onError: () => toast.error("Failed to grant access"),
  });
};

export const useRemoveMemberAccess = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => AgentAccessService.removeMemberAccess(agentId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_ACCESS_KEY(agentId) });
      toast.success("Access removed");
    },
    onError: () => toast.error("Failed to remove access"),
  });
};
